/**
 * useAsyncAction — the default seam for "click a button → kick off an
 * async mutation" without two recurring paper cuts:
 *
 *   1. Operator double-clicks a submit button → two POSTs land. Several
 *      forms (TestAuthoringTemplateDialog#handleSave, Hints#saveHint,
 *      ...) shipped without an `isSaving` gate because gating one button
 *      means threading a fresh state pair into a multi-call-site flow,
 *      and the convention was easy to forget.
 *   2. Operator clicks a destructive row action (Revoke, Remove, Delete)
 *      and the mutation fires instantly with no confirmation. The
 *      existing `useConfirm` primitive solves this — but call sites have
 *      to remember to pair `if (await confirm(...))` with each handler,
 *      and several didn't (ReferenceDocsSection delete, Slack channel
 *      remove, session revoke, the AccountSettings tokens revoke).
 *
 * Both gaps are "prevent unintended mutation" concerns that share the
 * same call-site shape. `useAsyncAction` bundles them so new mutations
 * pick up both gates by default rather than having to relitigate which
 * one a given surface needs.
 *
 *   const action = useAsyncAction({
 *     confirm: {
 *       title: 'Revoke this token?',
 *       description: "The token can't be undone. Anything using it stops working.",
 *       confirmLabel: 'Revoke token',
 *     },
 *     run: async () => { await api.revoke(id) },
 *     onSuccess: () => { reload() },
 *     onError: (err) => { setError(err.message) },
 *   })
 *
 *   <Button disabled={action.isRunning} onClick={action.trigger}>
 *     {action.isRunning ? 'Revoking…' : 'Revoke'}
 *   </Button>
 *
 * Behavior pins:
 *   - `trigger()` invocations while `isRunning` is true are no-ops. This
 *     is the double-click guard. A second click during the confirm
 *     dialog (before the underlying `run` starts) is also a no-op,
 *     because the existing `useConfirm` provider supersedes its prior
 *     unanswered confirm and resolves the older promise `false` — the
 *     stale `trigger()` exits via "confirm declined" without firing the
 *     mutation.
 *   - When the confirm gate resolves `false` (cancel/dismiss), `run` is
 *     NOT called and no `onSuccess` / `onError` fires.
 *   - `run` rejecting captures the error onto `error` and clears
 *     `isRunning`. `onError` (if provided) is invoked with the thrown
 *     value. Subsequent `trigger()`s clear the prior error before the
 *     confirm-gate / run sequence begins.
 *   - The hook returns `{ trigger, isRunning, error }`. No `reset` —
 *     errors clear on the next `trigger()` by design (the caller almost
 *     always wants the new attempt to start clean, and a separate
 *     `reset` ends up unused).
 *
 * Why a hook instead of expanding `useConfirm` itself:
 *   `useConfirm` is intentionally a thin primitive that just resolves a
 *   themed yes/no. Bundling the in-flight gate + error capture into the
 *   confirm hook would make the small `useConfirm` consumers (where the
 *   mutation already has its own state machine) carry weight they don't
 *   need. `useAsyncAction` composes `useConfirm` rather than absorbing
 *   it — sites that just want a yes/no still use `useConfirm` directly.
 */
import { useCallback, useRef, useState } from 'react'
import { useConfirm, type ConfirmOptions } from './useConfirm'

export interface UseAsyncActionOptions<T> {
  /** The async work the trigger should run. Required. The return value
   * is passed to `onSuccess`. */
  run: () => Promise<T>
  /** Optional confirm gate. When set, `trigger()` shows the themed
   * confirm dialog first and only invokes `run` on confirm. The
   * `destructive` field defaults to `true` (matching `useConfirm`'s
   * default — most action gates are destructive). */
  confirm?: ConfirmOptions
  /** Called with the run's resolved value on success. */
  onSuccess?: (value: T) => void
  /** Called with the thrown error on failure. The error is also
   * captured into the returned `error` state regardless of whether
   * this is provided. */
  onError?: (err: unknown) => void
}

export interface UseAsyncActionResult {
  /** Kick off the action. No-op while already running. The resolved
   * value of `run` is delivered via `onSuccess`, not the returned
   * promise — the promise here resolves `void` and only signals
   * completion of the whole gated sequence (confirm → run → success /
   * error callback). `void`-typed return keeps JSX onClick bindings
   * `() => action.trigger` clean without the lint nag about implicit-
   * `Promise<T>`-to-`void` conversion. */
  trigger: () => Promise<void>
  /** True from `trigger()` entry through the confirm gate + run
   * completion. Use as the source for `disabled={isRunning}` on the
   * triggering button. */
  isRunning: boolean
  /** The last error thrown by `run`, or `null`. Cleared on the next
   * `trigger()` invocation. */
  error: unknown | null
}

export function useAsyncAction<T = void>(
  options: UseAsyncActionOptions<T>,
): UseAsyncActionResult {
  const confirm = useConfirm()
  const [isRunning, setIsRunning] = useState(false)
  const [error, setError] = useState<unknown | null>(null)

  // Keep `options` reachable to the latest callback without re-creating
  // `trigger` on every render — call sites pass inline closures whose
  // referential identity changes each render, which would otherwise
  // churn any effect that depends on `trigger`. The ref captures the
  // latest version so each trigger uses the freshest closure.
  const optionsRef = useRef(options)
  optionsRef.current = options

  // The in-flight guard. A second `trigger()` while `isRunning` is true
  // is a no-op — this is the double-click defence. The ref mirrors the
  // state so the guard sees the latest value within the same tick (a
  // setState batch from the first click hasn't flushed when the second
  // click's handler entry runs).
  const inFlightRef = useRef(false)

  const trigger = useCallback(async () => {
    if (inFlightRef.current) return
    inFlightRef.current = true
    setError(null)
    setIsRunning(true)
    try {
      const opts = optionsRef.current
      if (opts.confirm) {
        const ok = await confirm(opts.confirm)
        if (!ok) {
          // Confirm declined — exit without running. The state pair is
          // cleared in `finally`.
          return
        }
      }
      try {
        const value = await opts.run()
        opts.onSuccess?.(value)
      } catch (err) {
        setError(err)
        opts.onError?.(err)
      }
    } finally {
      inFlightRef.current = false
      setIsRunning(false)
    }
  }, [confirm])

  return { trigger, isRunning, error }
}
